import { ThemeEvents } from '@theme/events';

/* Madson free-hat claim flow.
 *
 * ONE store, ONE resolver. Every surface (drawer gift card, pull-down gift
 * card, drawer unlock bar, header line) renders from resolveOffer() and
 * re-renders on the store's change notifications, so no surface can hold a
 * different opinion about eligibility, the cart, or the clock.
 *
 * Config comes from the <cart-special-eligibility> element that
 * layout/theme.liquid renders on every page: the magic token, the timer, the
 * gift product, the qualifying product types, and the server's count of
 * gift / pair units in the cart. A "pair" is a line whose product type is
 * in the qualifying list (snippets/cart-special-counts.liquid is the server
 * reading of the same rule); a hat, tee or case never counts.
 * cart:update (CartAddEvent / CartUpdateEvent in assets/events.js) keeps the
 * counts current without a reload; a bfcache restore re-reads /cart.js.
 *
 * localStorage record (madson-cart-special-eligibility):
 *   token           the magic-link token that made this visitor eligible
 *   capturedAt      first sight of the token
 *   expiresAt       capturedAt + 14 days. Eligibility, not the offer window.
 *   windowStartedAt the moment a qualifying pair (any line that is not the
 *                   gift) first landed in the cart; null until then.
 *                   deadline = windowStartedAt + timer minutes.
 *
 * Checkout bridge: eligibility only exists in this browser, and checkout runs
 * on Shopify's domain, so the cart attribute _free_hat_token carries it over.
 * The checkout UI extension (projects/madson-checkout) pitches the hat when
 * the attribute is non-empty. syncAttribute() keeps it equal to the magic
 * token while the visitor is eligible and clears it otherwise, at most one
 * write per pageview.
 */

const STORAGE_KEY = 'madson-cart-special-eligibility';
const ELIGIBILITY_DAYS = 14;
const ATTRIBUTE_KEY = '_free_hat_token';

/* States in which a surface has something to show. */
const SHOWN_STATES = new Set(['unlocked', 'claim', 'reserved', 'added']);

const store = {
  config: {
    enabled: false,
    token: '',
    magicOnly: true,
    minutes: 0,
    productId: 0,
    hatVariantIds: [],
    qualifyingTypes: [],
  },
  cart: { pairQty: 0, hatQty: 0 },
  serverItemCount: 0,
  /* True while the server-rendered cart is being confirmed against /cart.js;
   * every surface stays hidden until it lands, so nothing is painted from
   * counts that may be stale. */
  pending: false,
  /* The cart's current _free_hat_token attribute: null until known (from the
   * server render or /cart.js), then a string, '' when absent. */
  attribute: null,
  attributeWritten: false,
  listeners: new Set(),
  expiryTimer: null,
  inflight: null,
};

function readConfig() {
  const element = document.querySelector('cart-special-eligibility');
  if (!element) return;

  const data = element.dataset;
  const productId = Number(data.offerProductId) || 0;
  store.config = {
    enabled: productId > 0,
    token: (data.magicToken || '').trim(),
    magicOnly: data.magicOnly === 'true',
    minutes: parseInt(data.timerMinutes, 10) || 0,
    productId,
    hatVariantIds: (data.hatVariantIds || '').split(',').map(Number).filter(Boolean),
    qualifyingTypes: (data.qualifyingTypes || '')
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean),
  };
  store.cart = { pairQty: Number(data.pairQty) || 0, hatQty: Number(data.hatQty) || 0 };
  store.serverItemCount = Number(data.itemCount) || 0;
  store.attribute = typeof data.freeHatToken === 'string' ? data.freeHatToken : null;
}

/* The server-rendered counts can be stale: a history navigation is served
 * from the HTTP cache with the cart as it was before the add. Two signals,
 * the navigation type and the cart count assets/cart-icon.js keeps in
 * sessionStorage on every cart update. */
function serverCartLooksStale() {
  const navigation = performance.getEntriesByType?.('navigation')?.[0];
  if (navigation?.type === 'back_forward') return true;
  try {
    const stored = JSON.parse(sessionStorage.getItem('cart-count') || 'null');
    if (stored && Number(stored.value) !== store.serverItemCount) return true;
  } catch (_error) {
    // Storage unavailable: nothing to compare against.
  }
  return false;
}

/* ---------- eligibility record ---------- */

function readRecord() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch (_error) {
    return null;
  }
}

function writeRecord(record) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (_error) {
    // Storage unavailable. The current pageview keeps the in-memory decision.
  }
  return record;
}

/* The stored record, when it belongs to the configured token and has not
 * expired. */
function loadRecord() {
  const record = readRecord();
  if (!record || record.token !== store.config.token) return null;
  if (!(Number(record.expiresAt) > Date.now())) return null;
  return record;
}

function newRecord(windowStartedAt = null) {
  const now = Date.now();
  return writeRecord({
    token: store.config.token,
    capturedAt: now,
    expiresAt: now + ELIGIBILITY_DAYS * 86400000,
    windowStartedAt,
  });
}

function urlTokens() {
  return (new URLSearchParams(window.location.search).get('aca') || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

function deadlineFor(record) {
  const startedAt = Number(record?.windowStartedAt) || 0;
  if (store.config.minutes < 1 || !startedAt) return null;
  const span = store.config.minutes * 60000;
  let deadline = startedAt + span;
  /* Rolling window: the countdown is urgency, not a hard cutoff. A window that
   * has run out restarts from now rather than lapsing, so the offer never
   * disappears on a shopper who is still deciding. */
  if (deadline <= Date.now() && record) {
    record.windowStartedAt = Date.now();
    writeRecord(record);
    deadline = record.windowStartedAt + span;
  }
  return deadline;
}

/* Once per pageview. The magic link writes the record on first sight and
 * leaves a live one alone, so re-visiting the link never restarts a running
 * clock. A fresh click AFTER the window closed is an explicit request for the
 * offer: the anchor clears, so the clock starts again at the next pair-add,
 * or right now (syncAnchor) when a pair is already in the cart. */
function captureFromUrl() {
  const { token } = store.config;
  if (!token || !urlTokens().includes(token)) return;

  const record = loadRecord();
  if (!record) {
    newRecord();
    return;
  }

  const deadline = deadlineFor(record);
  if (deadline !== null && deadline <= Date.now()) {
    record.windowStartedAt = null;
    writeRecord(record);
  }
}

/* Keeps windowStartedAt in step with the cart. Runs at init and after every
 * cart sync: the clock starts the first time a pair is present, and a cart
 * that loses its last pair drops the anchor so the next pair starts a fresh
 * clock. */
function syncAnchor() {
  const record = loadRecord();

  if (store.cart.pairQty < 1) {
    if (record?.windowStartedAt) {
      record.windowStartedAt = null;
      writeRecord(record);
    }
    return;
  }

  if (record) {
    if (!record.windowStartedAt) {
      record.windowStartedAt = Date.now();
      writeRecord(record);
    }
    return;
  }

  /* No record. With the magic gate on that means not eligible: nothing to
   * anchor. With the gate off everyone is eligible, and the anchor still
   * needs a home. */
  if (!store.config.magicOnly) newRecord(Date.now());
}

/* ---------- cart ---------- */

/* The qualifying rule, as the server applies it: a line is a pair when its
 * product type is in the merchant's list. /cart.js carries product_type on
 * every line, so a line added without a reload is judged by exactly the rule
 * the server used for the ones it rendered. */
function lineQualifies(item) {
  const type = String(item.product_type || '')
    .trim()
    .toLowerCase();
  return type !== '' && store.config.qualifyingTypes.includes(type);
}

function countItems(items) {
  let pairQty = 0;
  let hatQty = 0;
  for (const item of items) {
    const quantity = Number(item.quantity) || 0;
    if (Number(item.product_id) === store.config.productId) hatQty += quantity;
    else if (lineQualifies(item)) pairQty += quantity;
  }
  return { pairQty, hatQty };
}

/* Feed the store a /cart.js items array (from a cart event, or a fetch a
 * surface already made). Idempotent. */
export function syncCart(items) {
  if (!store.config.enabled || !Array.isArray(items)) return;
  store.cart = countItems(items);
  syncAnchor();
  notify();
}

/* The cart's attributes, from any /cart.js payload (a fetch or a cart event's
 * resource). Nothing to learn from a payload that has none. */
function readAttributes(cart) {
  const attributes = cart?.attributes;
  if (!attributes || typeof attributes !== 'object') return;
  const value = attributes[ATTRIBUTE_KEY];
  store.attribute = value == null ? '' : String(value);
}

/* Keep the cart attribute in step with eligibility. Runs after every
 * resolution; writes at most once per pageview, and only when the cart's
 * current value differs, so it can never loop. Fire-and-forget: no surface
 * renders from the attribute, and the write is deliberately NOT announced as
 * a cart:update (nothing on the page changes). */
function syncAttribute() {
  if (!store.config.enabled || store.pending || store.attributeWritten) return;
  if (store.attribute === null) return;

  const want = resolveOffer().eligible ? store.config.token : '';
  if (store.attribute === want) return;

  store.attributeWritten = true;
  store.attribute = want;
  const root = window.Shopify?.routes?.root || '/';
  fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [ATTRIBUTE_KEY]: want } }),
  }).catch(() => {
    // Storefront unreachable: checkout simply will not pitch the hat this time.
  });
}

/* Re-read the cart from the server. One request at a time; callers awaiting
 * the same refresh share it. */
export function refreshCart() {
  if (!store.config.enabled) return Promise.resolve(null);
  if (!store.inflight) {
    const root = window.Shopify?.routes?.root || '/';
    store.inflight = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .then((cart) => {
        store.inflight = null;
        readAttributes(cart);
        if (Array.isArray(cart?.items)) syncCart(cart.items);
        return cart;
      });
  }
  return store.inflight;
}

/* Horizon's cart events carry the /cart.js resource when the theme fetched
 * it. The gift form's own add does not (product-form.js only reads
 * input[name="id"], and the gift picks its size with a <select>), so the
 * fallback is a fetch. */
function onCartUpdate(event) {
  const resource = event.detail?.resource;
  if (Array.isArray(resource?.items)) {
    readAttributes(resource);
    syncCart(resource.items);
  } else {
    refreshCart();
  }
}

/* ---------- resolver ---------- */

/**
 * The one decision every surface renders from.
 *
 * state:
 *   ineligible  nothing renders
 *   pending     the cart is being confirmed against /cart.js: nothing renders yet
 *   unlocked    eligible, no pair in the cart: "add any pair" pitch, no timer
 *   claim       pair in, hat not in, window open: claim card + countdown
 *   reserved    hat in, window open: "reserved for" + countdown
 *   added       hat in, window closed (or no timer): "free hat in your cart"
 *   lapsed      pair in, hat not in, window closed: every surface hides
 */
export function resolveOffer() {
  const { enabled, magicOnly, hatVariantIds } = store.config;
  const record = enabled ? loadRecord() : null;
  const eligible = enabled && (Boolean(record) || !magicOnly);
  const pairInCart = store.cart.pairQty > 0;
  const hatInCart = store.cart.hatQty > 0;
  const deadline = eligible && pairInCart ? deadlineFor(record) : null;
  const windowOpen = eligible && pairInCart && (deadline === null || deadline > Date.now());

  let state = 'ineligible';
  if (!eligible) state = 'ineligible';
  else if (store.pending) state = 'pending';
  else if (!pairInCart) state = 'unlocked';
  else if (hatInCart) state = windowOpen && deadline !== null ? 'reserved' : 'added';
  else if (windowOpen) state = 'claim';
  else state = 'claim'; /* expiry keeps the offer claimable, countdown just stops */

  return { eligible, pairInCart, hatInCart, windowOpen, deadline, hatVariantIds, state };
}

/* ---------- change notifications ---------- */

export function subscribeOffer(listener) {
  store.listeners.add(listener);
  return () => store.listeners.delete(listener);
}

function notify() {
  scheduleExpiry();
  for (const listener of store.listeners) listener();
  syncAttribute();
}

/* One timeout for the moment the window closes, so surfaces without their own
 * countdown (the drawer bar) lapse in the same second as the ones with one. */
function scheduleExpiry() {
  clearTimeout(store.expiryTimer);
  store.expiryTimer = null;

  const { deadline } = resolveOffer();
  if (deadline === null) return;
  const wait = deadline - Date.now();
  if (wait <= 0) return;
  store.expiryTimer = setTimeout(notify, wait + 100);
}

/* ---------- countdown ---------- */

const pad = (value) => String(value).padStart(2, '0');

function formatRemaining(remaining) {
  const totalSeconds = Math.floor(remaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

function paintCountdown(host, deadline, onExpire) {
  const remaining = deadline - Date.now();
  if (remaining <= 0) {
    stopCountdown(host);
    onExpire();
    return;
  }
  const text = formatRemaining(remaining);
  for (const output of host.querySelectorAll('[data-cart-special-timer]')) {
    if (output.textContent !== text) output.textContent = text;
  }
}

/* Paints mm:ss (h:mm:ss above an hour) into every [data-cart-special-timer]
 * in the host once a second, off the shared deadline. Idempotent: a call with
 * the deadline already running only repaints. onExpire fires once the
 * deadline passes, so the host re-resolves. */
function startCountdown(host, deadline, onExpire) {
  if (!host.timerId || host.timerDeadline !== deadline) {
    stopCountdown(host);
    host.timerDeadline = deadline;
    host.timerId = setInterval(() => paintCountdown(host, deadline, onExpire), 1000);
  }
  /* Written on EVERY render, not only when the interval starts. The pill's
   * CSS keys on this attribute, and Horizon's morph (the header hydration on
   * every page load, and every cart:update) patches the server's attribute
   * set back onto the host, which never carries it. The observer then
   * re-renders; when the interval was already running this used to return
   * before restoring the attribute, leaving the card up with no timer. */
  setAttr(host, 'data-timer-active', 'true');
  paintCountdown(host, deadline, onExpire);
}

function stopCountdown(host) {
  if (host.timerId) clearInterval(host.timerId);
  host.timerId = null;
  host.timerDeadline = null;
  if (host.hasAttribute('data-timer-active')) host.removeAttribute('data-timer-active');
}

/* ---------- surfaces ---------- */

function setHidden(element, hidden) {
  if (element.hidden !== hidden) element.hidden = hidden;
}

function setAttr(element, name, value) {
  if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}

/* Every surface is rendered `hidden` by the server, because eligibility is
 * only knowable on the client. Horizon morphs the cart in place on every
 * update, which patches those server attributes back on WITHOUT
 * disconnecting the element, so connectedCallback never runs again. Watching
 * the attributes and re-rendering re-asserts the resolver's decision; render
 * only writes what differs, so the observer cannot loop. */
function observeMorph(element, render) {
  if (element.morphObserver) return;
  element.morphObserver = new MutationObserver(render);
  element.morphObserver.observe(element, {
    attributes: true,
    attributeFilter: ['hidden', 'data-state', 'data-timer-active'],
  });
}

class OfferSurface extends HTMLElement {
  connectedCallback() {
    this.unsubscribe = subscribeOffer(this.render);
    observeMorph(this, this.render);
    this.render();
  }

  disconnectedCallback() {
    stopCountdown(this);
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.morphObserver?.disconnect();
    this.morphObserver = null;
  }

  render = () => {};
}

/* The claim card: in the drawer (snippets/cart-special-offer.liquid) and in
 * the post-add pull-down (snippets/cart-added-drop.liquid), one snippet for
 * both (snippets/cart-special-offer-card.liquid). Shows only while the gift
 * can still be claimed. */
class CartSpecialOffer extends OfferSurface {
  render = () => {
    const offer = resolveOffer();
    const show = offer.state === 'claim';
    if (show && offer.deadline !== null) startCountdown(this, offer.deadline, this.render);
    else stopCountdown(this);
    setHidden(this, !show);
  };
}

/* The persistent "gift unlocked" bar at the top of the drawer. It stays
 * through the claim and once the hat is in; it only leaves when the pitch has
 * lapsed. */
class CartUnlockBar extends OfferSurface {
  connectedCallback() {
    window.addEventListener('pageshow', this.onPageShow);
    super.connectedCallback();
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.onPageShow);
    super.disconnectedCallback();
  }

  /* Restored from the bfcache: the cart may have changed on the page in
   * between (same pattern as assets/cart-icon.js). */
  onPageShow = (event) => {
    if (event.persisted) refreshCart();
  };

  render = () => {
    const { state } = resolveOffer();
    /* The bar shows in `unlocked` too, when nothing qualifying is in the bag
     * yet, so the copy has to switch or a hoodie-only cart reads "your free
     * gift is unlocked" and the customer expects a hat. The snippet renders
     * both lines; CSS picks one off this attribute. */
    setAttr(this, 'data-state', state);
    setHidden(this, !SHOWN_STATES.has(state));
  };
}

/* The line above the header. data-state picks the copy (see the snippet);
 * claim and reserved carry the countdown; unlocked opens the gift popup, the
 * rest open the drawer. */
class CartUnlockAnnouncement extends OfferSurface {
  connectedCallback() {
    this.link = this.querySelector('.cart-unlock-announcement__link');
    this.link?.addEventListener('click', this.onClick);
    window.addEventListener('pageshow', this.onPageShow);
    super.connectedCallback();
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.onClick);
    window.removeEventListener('pageshow', this.onPageShow);
    super.disconnectedCallback();
  }

  onPageShow = (event) => {
    if (event.persisted) refreshCart();
  };

  /* unlocked: the gift popup (snippets/cart-unlock-popup.liquid), the href
   * keeps the sunglasses collection as the fallback. Every other state: the
   * drawer where there is one (the same call cart-added-drop.js makes), the
   * href keeps /cart as the fallback. Both are Horizon dialog-components. */
  onClick = (event) => {
    const target = this.dialogTarget();
    if (!target) return;
    event.preventDefault();
    target.showDialog();
  };

  /* The dialog a tap opens in the current state, or null when this page has
   * none (then the link simply navigates). */
  dialogTarget() {
    const element =
      this.getAttribute('data-state') === 'unlocked'
        ? document.getElementById('cart-unlock-popup')
        : document.querySelector('cart-drawer-component');
    return element && typeof element.showDialog === 'function' ? element : null;
  }

  /* The popup's hat image is rendered lazy so visitors who never see the
   * line never fetch it. WebKit's lazy-load check never re-fires inside a
   * closed dialog, so the first unlocked reveal flips it to eager: the image
   * is in the cache before the tap, and only eligible shoppers paid for it. */
  primePopupImage() {
    const image = document.querySelector('#cart-unlock-popup img[loading="lazy"]');
    if (image) image.setAttribute('loading', 'eager');
  }

  render = () => {
    const offer = resolveOffer();
    if (!SHOWN_STATES.has(offer.state)) {
      stopCountdown(this);
      setHidden(this, true);
      return;
    }

    setAttr(this, 'data-state', offer.state);

    const href = offer.state === 'unlocked' ? this.dataset.unlockedHref : this.dataset.reservedHref;
    if (this.link && href && this.link.getAttribute('href') !== href) this.link.href = href;

    /* Tells assistive tech the tap opens a dialog, when one exists here.
     * Re-written on every render, like data-timer-active. */
    if (this.link) {
      if (this.dialogTarget()) setAttr(this.link, 'aria-haspopup', 'dialog');
      else if (this.link.hasAttribute('aria-haspopup')) this.link.removeAttribute('aria-haspopup');
    }

    if (offer.state === 'unlocked') this.primePopupImage();

    const timed = offer.state === 'claim' || offer.state === 'reserved';
    if (timed && offer.deadline !== null) startCountdown(this, offer.deadline, this.render);
    else stopCountdown(this);

    setHidden(this, false);
  };
}

/* ---------- init ---------- */

readConfig();

if (store.config.enabled) {
  captureFromUrl();

  /* The anchor decision is only as good as the counts it is made from. When
   * the page may be stale, or looks like it lost its last pair while a window
   * was running (which would drop the anchor), confirm against /cart.js first
   * and keep every surface hidden until the answer is in. */
  const record = loadRecord();
  const mustConfirm = serverCartLooksStale() || (store.cart.pairQty < 1 && Boolean(record?.windowStartedAt));
  if (mustConfirm) {
    store.pending = true;
    refreshCart().then((cart) => {
      if (!Array.isArray(cart?.items)) syncAnchor();
      store.pending = false;
      notify();
    });
  } else {
    syncAnchor();
    syncAttribute();
  }

  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCart();
  });
  /* Timers are throttled in background tabs; a tab that comes back re-resolves
   * against the clock straight away. */
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) notify();
  });
  scheduleExpiry();
}

if (!customElements.get('cart-special-offer')) {
  customElements.define('cart-special-offer', CartSpecialOffer);
}

if (!customElements.get('cart-unlock-bar')) {
  customElements.define('cart-unlock-bar', CartUnlockBar);
}

if (!customElements.get('cart-unlock-announcement')) {
  customElements.define('cart-unlock-announcement', CartUnlockAnnouncement);
}
